import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import { useYardTaskFetcher } from '@satellite/composables/useYardTaskFetcher';
import { useYardTaskPresenter } from '@satellite/composables/useYardTaskPresenter';
import useYard from '@/modules/yard/composables/useYard';

import type { Maybe, TaskViewData } from '@nova/core';
import type { ComputedRef, Ref } from 'vue';

export type UseYardTaskOptions = {
  /**
   * Task ID to fetch. Can be a ref for reactivity.
   * Optional if task (with id) is provided.
   */
  taskId?: Ref<string | null | undefined>;

  /**
   * Optional task object. Used as placeholderData while the query loads/refetches.
   * Effective id is derived from taskId or task.id (explicit taskId wins; mismatch triggers a warning).
   */
  task?: Ref<Maybe<TaskViewData>> | ComputedRef<Maybe<TaskViewData>> | TaskViewData;

  /**
   * If true, the task will be refreshed automatically when the task is updated (via eventHub).
   * Default: true
   */
  live?: boolean;
  /**
   * Stale time in milliseconds.
   * Default: Infinity (never stale, until a Subspace event invalidates the query; manual refresh is always available)
   */
  staleTime?: number;
  /**
   * Callback for when task is updated.
   */
  onUpdateReceived?: (task: TaskViewData) => void;
};

export function useYardTask(options: UseYardTaskOptions) {
  const { taskId, task: taskOption, live, staleTime, onUpdateReceived } = options;
  const yardStore = useYard();
  const { t } = useI18n();

  const warehouseTimezone = computed(() => {
    return yardStore.selectedWarehouseRef?.value?.timezone;
  });

  const fetcher = useYardTaskFetcher(
    { taskId, task: taskOption, live, staleTime, onUpdateReceived },
    { warehouseTimezone }
  );

  const presenter = useYardTaskPresenter(fetcher.task, {
    warehouseTimezone,
    i18n: t
  });

  return {
    // Fetching state
    isLoading: fetcher.isLoading,
    isFetching: fetcher.isFetching,
    error: fetcher.error,

    refresh: fetcher.refresh,
    invalidate: fetcher.invalidate,

    onIntersect: fetcher.onIntersect,

    // Tasks
    rawTask: fetcher.rawTask,
    task: fetcher.task, // View-ready version from fetcher

    // All presenter properties (including events from ViewData)
    ...presenter,

    // Custom formatters
    formatDueDate: (dueDate?: string | null) => {
      const formatted = presenter.formatDate(dueDate || null);
      return formatted || t('yard.pages.yardTasks.messages.noDueDate');
    },
    formatCreatedAt: (createdAt?: string | null) => {
      const formatted = presenter.formatDate(createdAt || null);
      return formatted || '--';
    }
  };
}
